import React, { useState } from 'react';
import { X, Calendar, Users, AlertCircle, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { parsearFechaCaracas, formatearFechaConDiaCaracas } from '../utils/dateUtils';
import CrearCitaManualModal from './CrearCitaManualModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConfiguracionDiaModal = ({ dia, configGlobal, onClose, onSave }) => {
  const [cupos, setCupos] = useState(dia.cupos_totales);
  const [operativo, setOperativo] = useState(dia.operativo);
  const [motivo, setMotivo] = useState(dia.motivo || '');
  const [guardando, setGuardando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [showCrearCitaModal, setShowCrearCitaModal] = useState(false);

  const fecha = parsearFechaCaracas(dia.fecha);
  const fechaFormateada = formatearFechaConDiaCaracas(dia.fecha);

  const handleGuardar = async () => {
    if (!operativo && !motivo.trim()) {
      toast.error('Debes indicar un motivo si el día no es operativo');
      return;
    }

    if (operativo && cupos < dia.citas_agendadas) {
      toast.error(`No puedes reducir los cupos por debajo de las citas ya agendadas (${dia.citas_agendadas})`);
      return;
    }

    setGuardando(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/gestor-citas/dia-configuracion`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fecha: dia.fecha,
          cupos_disponibles: cupos,
          operativo: operativo,
          motivo: operativo ? null : motivo
        })
      });

      const isOk = response.ok;
      const statusCode = response.status;
      
      let data = {};
      try {
        data = await response.json();
      } catch (jsonError) {
        console.log('No se pudo leer JSON de la respuesta');
      }
      
      if (isOk) {
        toast.success('Configuración del día actualizada');
        onSave();
      } else {
        toast.error(data?.detail || `Error al actualizar (${statusCode})`);
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.message && error.message.includes('Body has already been consumed')) {
        toast.success('Configuración actualizada');
        setTimeout(() => onSave(), 500);
      } else {
        toast.error('Error al guardar');
      }
    } finally {
      setGuardando(false);
    }
  };

  const handleRestaurarDefault = async () => {
    if (!window.confirm('¿Restaurar configuración por defecto para este día?')) return;

    setEliminando(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/gestor-citas/dia-configuracion/${dia.fecha}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const isOk = response.ok;
      const statusCode = response.status;

      let data = {};
      try {
        data = await response.json();
      } catch (jsonError) {
        console.log('No se pudo leer JSON de la respuesta');
      }

      if (isOk) {
        toast.success('Configuración restaurada');
        onSave();
      } else {
        toast.error(data?.detail || `Error al restaurar (${statusCode})`);
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.message && error.message.includes('Body has already been consumed')) {
        toast.success('Configuración restaurada');
        setTimeout(() => onSave(), 500);
      } else {
        toast.error('Error al restaurar');
      }
    } finally {
      setEliminando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-gray-700 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-yellow-500" />
            <div>
              <h2 className="text-xl font-bold text-white capitalize">
                {fechaFormateada}
              </h2>
              <p className="text-gray-400 text-sm">Fecha: {dia.fecha}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Estado actual */}
          <div className="bg-slate-800 border border-gray-700 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-3">Estado Actual</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-gray-400 text-sm">Citas Agendadas</p>
                <p className="text-2xl font-bold text-white">{dia.citas_agendadas}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Cupos Totales</p>
                <p className="text-2xl font-bold text-yellow-500">{dia.cupos_totales}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Disponibles</p>
                <p className={`text-2xl font-bold ${dia.cupos_disponibles > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {dia.cupos_disponibles}
                </p>
              </div>
            </div>
          </div>

          {/* Citas del día */}
          {dia.citas && dia.citas.length > 0 && (
            <div className="bg-slate-800 border border-gray-700 rounded-lg p-4">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Citas Agendadas ({dia.citas.length})
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {dia.citas.map((cita) => (
                  <div key={cita.id} className="bg-slate-700 rounded p-3 text-sm">
                    <p className="text-white font-semibold">
                      {cita.solicitante?.nombre_completo || 'Sin nombre'}
                    </p>
                    <p className="text-gray-400">
                      Exp: {cita.numero_expediente} | {new Date(cita.cita?.fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Configuración */}
          <div className="space-y-4">
            <h3 className="text-white font-semibold">Configuración del Día</h3>
            
            {/* Toggle operativo */}
            <div className="flex items-center justify-between bg-slate-800 border border-gray-700 rounded-lg p-4">
              <div>
                <p className="text-white font-semibold">Día Operativo</p>
                <p className="text-gray-400 text-sm">
                  {operativo ? 'Los clientes pueden agendar citas' : 'No disponible para clientes'}
                </p>
              </div>
              <button
                onClick={() => setOperativo(!operativo)}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  operativo ? 'bg-green-500' : 'bg-red-500'
                }`}
              >
                <div
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    operativo ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {operativo ? (
              /* Cupos disponibles */
              <div>
                <label className="block text-white font-semibold mb-2">
                  Cupos Disponibles
                </label>
                <p className="text-gray-400 text-sm mb-2">
                  Mínimo: {dia.citas_agendadas} (citas ya agendadas)
                </p>
                <input
                  type="number"
                  min={dia.citas_agendadas}
                  max="50"
                  value={cupos}
                  onChange={(e) => setCupos(parseInt(e.target.value))}
                  className="w-full bg-slate-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-lg font-semibold focus:outline-none focus:border-yellow-500"
                />
              </div>
            ) : (
              /* Motivo de no operativo */
              <div>
                <label className="block text-white font-semibold mb-2">
                  Motivo (Visible para clientes)
                </label>
                <p className="text-gray-400 text-sm mb-2">
                  Ejemplo: "Feriado Nacional", "Mantenimiento", "Evento Especial"
                </p>
                <input
                  type="text"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Escribe el motivo..."
                  className="w-full bg-slate-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-yellow-500"
                />
                
                {dia.citas_agendadas > 0 && (
                  <div className="mt-3 bg-red-900/20 border border-red-700 rounded-lg p-3 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-red-400 font-semibold">Atención</p>
                      <p className="text-gray-400">
                        Ya hay {dia.citas_agendadas} cita(s) agendada(s) para este día. 
                        Deberás gestionarlas manualmente antes de marcar el día como no operativo.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Info de configuración global */}
          {configGlobal && (
            <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
              <p className="text-blue-400 text-sm">
                <strong>Configuración Global:</strong> {configGlobal.cupos_por_dia} cupos por día. 
                Si restauras este día, usará estos valores por defecto.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900 border-t border-gray-700 p-6">
          <div className="flex justify-between items-center mb-4">
            <Button
              onClick={() => setShowCrearCitaModal(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
            >
              <Calendar className="w-4 h-4 mr-2" />
              + Crear Cita Manual
            </Button>
          </div>
          
          <div className="flex justify-between">
            <Button
              onClick={handleRestaurarDefault}
              variant="outline"
              className="border-gray-700 text-gray-400 hover:bg-gray-800"
              disabled={guardando || eliminando}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Restaurar Default
            </Button>
            
            <div className="flex gap-3">
              <Button
                onClick={onClose}
                variant="outline"
                className="border-gray-700 text-gray-400 hover:bg-gray-800"
                disabled={guardando || eliminando}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleGuardar}
                className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold"
                disabled={guardando || eliminando}
              >
                {guardando ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Modal de Crear Cita Manual */}
        {showCrearCitaModal && (
          <CrearCitaManualModal
            fecha={dia.fecha}
            onClose={() => setShowCrearCitaModal(false)}
            onSuccess={() => {
              setShowCrearCitaModal(false);
              toast.success('Cita manual creada exitosamente');
              onSave();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ConfiguracionDiaModal;